import { useEffect, useState, useCallback } from "react";
import { api, money, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Users, DollarSign, Check, Clock } from "lucide-react";

const COMM_FILTERS = ["all", "pending", "paid"];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return String(iso || "").slice(0, 10);
  }
};

export const AdminAffiliates = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commFilter, setCommFilter] = useState("all");
  const [affFilter, setAffFilter] = useState("all");
  const [updatingId, setUpdatingId] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", discount_pct: 15, commission_pct: 15 });

  const loadAffiliates = useCallback(() => {
    return api.get("/admin/affiliates").then((r) => setAffiliates(r.data)).catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
  }, []);

  const loadCommissions = useCallback(() => {
    const params = [];
    if (affFilter !== "all") params.push(`affiliate=${affFilter}`);
    if (commFilter !== "all") params.push(`status=${commFilter}`);
    const qs = params.length ? `?${params.join("&")}` : "";
    return api.get(`/admin/commissions${qs}`).then((r) => setCommissions(r.data)).catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
  }, [affFilter, commFilter]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadAffiliates(), loadCommissions()]).finally(() => setLoading(false));
  }, [loadAffiliates, loadCommissions]);

  const createAffiliate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/affiliates", {
        code: form.code, name: form.name || form.code,
        discount_pct: parseFloat(form.discount_pct) || 0,
        commission_pct: parseFloat(form.commission_pct) || 0,
      });
      toast.success("Affiliate code created");
      setShowForm(false);
      setForm({ code: "", name: "", discount_pct: 15, commission_pct: 15 });
      await loadAffiliates();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (a) => {
    try {
      await api.put(`/admin/affiliates/${a.code}`, { active: !a.active });
      setAffiliates((prev) => prev.map((x) => (x.code === a.code ? { ...x, active: !x.active } : x)));
      toast.success(a.active ? `${a.code} disabled` : `${a.code} enabled`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setCommissionStatus = async (c, status) => {
    setUpdatingId(c.id);
    try {
      await api.post(`/admin/commissions/${c.id}/status`, { status });
      setCommissions((prev) => prev.map((x) => (x.id === c.id ? { ...x, status } : x)));
      await loadAffiliates();
      toast.success(status === "paid" ? "Marked paid" : "Marked pending");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUpdatingId("");
    }
  };

  const totalPending = affiliates.reduce((s, a) => s + (a.stats?.commission_pending || 0), 0);
  const totalPaid = affiliates.reduce((s, a) => s + (a.stats?.commission_paid || 0), 0);
  const totalSales = affiliates.reduce((s, a) => s + (a.stats?.sales || 0), 0);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="admin-affiliates">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <Stat icon={Users} label="Affiliates" value={affiliates.length} testid="aff-stat-count" />
        <Stat icon={DollarSign} label="Attributed Sales" value={money(totalSales)} testid="aff-stat-sales" />
        <Stat icon={Clock} label="Commission Owed" value={money(totalPending)} accent testid="aff-stat-pending" />
        <Stat icon={Check} label="Commission Paid" value={money(totalPaid)} testid="aff-stat-paid" />
      </div>

      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display font-bold text-lg tracking-tight uppercase text-white">Affiliate Codes</h3>
        <button onClick={() => setShowForm(true)} className="btn-primary inline-flex items-center gap-2 px-5 py-3 text-xs tracking-[0.2em] uppercase font-bold" data-testid="aff-add-btn">
          <Plus size={16} /> New Code
        </button>
      </div>

      {loading ? (
        <p className="py-20 text-center text-sm tracking-widest uppercase text-white/60">Loading...</p>
      ) : (
        <>
          <div className="border border-white/15 sq-lg overflow-hidden mb-12" data-testid="aff-table">
            <div className="hidden md:grid grid-cols-[120px_1fr_90px_100px_90px_120px_130px_120px] border-b border-white/15 bg-electric text-black text-[10px] tracking-[0.2em] uppercase font-bold">
              <div className="p-3">Code</div>
              <div className="p-3">Name</div>
              <div className="p-3">Off</div>
              <div className="p-3">Comm.</div>
              <div className="p-3">Orders</div>
              <div className="p-3">Owed</div>
              <div className="p-3">Paid</div>
              <div className="p-3 text-right">Status</div>
            </div>
            {affiliates.length === 0 ? (
              <p className="py-16 text-center text-sm tracking-[0.2em] uppercase text-white/40" data-testid="aff-empty">No affiliate codes yet</p>
            ) : affiliates.map((a) => (
              <div key={a.code} className="grid grid-cols-2 md:grid-cols-[120px_1fr_90px_100px_90px_120px_130px_120px] items-center border-b border-white/12 last:border-b-0" data-testid={`aff-row-${a.code}`}>
                <div className="p-3 font-display font-bold text-sm uppercase text-electric">{a.code}</div>
                <div className="p-3 text-xs text-white/70 truncate">{a.name}</div>
                <div className="p-3 text-xs text-white/70">{a.discount_pct}%</div>
                <div className="p-3 text-xs text-white/70">{a.commission_pct}%</div>
                <div className="p-3 text-xs text-white/70">{a.stats?.orders || 0}</div>
                <div className="p-3 font-display font-bold text-sm text-white">{money(a.stats?.commission_pending || 0)}</div>
                <div className="p-3 text-xs text-white/50">{money(a.stats?.commission_paid || 0)}</div>
                <div className="p-3 md:text-right">
                  <button
                    onClick={() => toggleActive(a)}
                    className={`inline-block text-[10px] tracking-[0.12em] uppercase font-bold border px-2.5 py-1 sq-sm sq-press transition-colors duration-200 ${a.active ? "bg-emerald-400/10 text-emerald-300 border-emerald-400/40" : "bg-white/5 text-white/40 border-white/20"}`}
                    data-testid={`aff-toggle-${a.code}`}
                  >
                    {a.active ? "Active" : "Disabled"}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Commissions */}
          <h3 className="font-display font-bold text-lg tracking-tight uppercase text-white mb-5">Commission Ledger</h3>
          <div className="flex flex-wrap gap-2 mb-5" data-testid="comm-filters">
            <select value={affFilter} onChange={(e) => setAffFilter(e.target.value)} className="bg-[#0C1128] border border-white/25 text-white px-4 py-2 text-[11px] tracking-[0.15em] uppercase font-bold outline-none focus:border-electric sq-sm" data-testid="comm-filter-affiliate">
              <option value="all">All codes</option>
              {affiliates.map((a) => <option key={a.code} value={a.code}>{a.code}</option>)}
            </select>
            {COMM_FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setCommFilter(f)}
                className={`px-4 py-2 text-[10px] tracking-[0.2em] uppercase font-bold border transition-colors duration-200 sq-md sq-press ${commFilter === f ? "bg-electric text-black border-electric" : "bg-transparent text-white border-white/25 hover:border-electric hover:text-electric"}`}
                data-testid={`comm-filter-${f}`}
              >
                {f === "all" ? "All" : f}
              </button>
            ))}
          </div>

          <div className="border border-white/15 sq-lg overflow-hidden" data-testid="comm-table">
            <div className="hidden md:grid grid-cols-[110px_1fr_110px_110px_100px_120px_130px] border-b border-white/15 bg-electric text-black text-[10px] tracking-[0.2em] uppercase font-bold">
              <div className="p-3">Code</div>
              <div className="p-3">Order / Customer</div>
              <div className="p-3">Date</div>
              <div className="p-3">Type</div>
              <div className="p-3">Base</div>
              <div className="p-3">Commission</div>
              <div className="p-3 text-right">Status</div>
            </div>
            {commissions.length === 0 ? (
              <p className="py-16 text-center text-sm tracking-[0.2em] uppercase text-white/40" data-testid="comm-empty">No commissions yet</p>
            ) : commissions.map((c) => (
              <div key={c.id} className="grid grid-cols-2 md:grid-cols-[110px_1fr_110px_110px_100px_120px_130px] items-center border-b border-white/12 last:border-b-0" data-testid={`comm-row-${c.id}`}>
                <div className="p-3 font-display font-bold text-xs uppercase text-electric">{c.affiliate_code}</div>
                <div className="p-3 min-w-0">
                  <div className="text-xs font-bold text-white truncate">{c.order_number}</div>
                  <div className="text-[10px] text-white/40 truncate">{c.customer_email}</div>
                </div>
                <div className="p-3 text-xs text-white/60">{fmtDate(c.created_at)}</div>
                <div className="p-3 text-[10px] tracking-[0.12em] uppercase text-white/60">{c.type === "subscription" ? "Subscription" : "One-time"}</div>
                <div className="p-3 text-xs text-white/60">{money(c.commission_base)}</div>
                <div className="p-3 font-display font-bold text-sm text-white">{money(c.commission_amount)}</div>
                <div className="p-3 md:text-right">
                  {c.status === "paid" ? (
                    <button
                      onClick={() => setCommissionStatus(c, "pending")}
                      disabled={updatingId === c.id}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] tracking-[0.15em] uppercase font-bold border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 transition-colors duration-200 sq-sm sq-press disabled:opacity-50"
                      data-testid={`comm-status-${c.id}`}
                    >
                      {updatingId === c.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Paid
                    </button>
                  ) : (
                    <button
                      onClick={() => setCommissionStatus(c, "paid")}
                      disabled={updatingId === c.id}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] tracking-[0.15em] uppercase font-bold border border-electric/50 text-electric hover:bg-electric/10 transition-colors duration-200 sq-sm sq-press disabled:opacity-50"
                      data-testid={`comm-status-${c.id}`}
                    >
                      {updatingId === c.id ? <Loader2 size={13} className="animate-spin" /> : <DollarSign size={13} />} Mark Paid
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* New affiliate modal */}
      {showForm && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-start justify-center overflow-y-auto py-10 px-4">
          <div className="bg-[#0B1024] border border-white/15 w-full max-w-md my-auto sq-xl" data-testid="aff-form">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/15">
              <h2 className="font-display font-bold text-lg tracking-tighter uppercase text-white">New Affiliate Code</h2>
              <button onClick={() => setShowForm(false)} className="text-white/70 hover:text-electric text-2xl leading-none" data-testid="aff-form-close">×</button>
            </div>
            <form onSubmit={createAffiliate} className="p-6 space-y-4">
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase font-bold block mb-2 text-white/70">Code *</label>
                <input required value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="e.g. JORDAN" className="cl-input" data-testid="aff-field-code" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase font-bold block mb-2 text-white/70">Affiliate Name</label>
                <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Display name" className="cl-input" data-testid="aff-field-name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase font-bold block mb-2 text-white/70">Discount %</label>
                  <input type="number" step="1" value={form.discount_pct} onChange={(e) => set("discount_pct", e.target.value)} className="cl-input" data-testid="aff-field-discount" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase font-bold block mb-2 text-white/70">Commission %</label>
                  <input type="number" step="1" value={form.commission_pct} onChange={(e) => set("commission_pct", e.target.value)} className="cl-input" data-testid="aff-field-commission" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} className="btn-primary flex-1 py-4 text-xs tracking-[0.2em] uppercase font-bold disabled:opacity-50" data-testid="aff-form-save">
                  {saving ? "Saving..." : "Create Code"}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="btn-ghost px-8 py-4 text-xs tracking-[0.2em] uppercase font-bold">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, accent, testid }) => (
  <div className={`border p-4 sq-lg ${accent ? "border-electric/50 bg-electric/5" : "border-white/15 bg-[#0B1024]"}`} data-testid={testid}>
    <div className="flex items-center gap-2 text-white/50 mb-2">
      <Icon size={14} className={accent ? "text-electric" : ""} />
      <span className="text-[10px] tracking-[0.2em] uppercase font-bold">{label}</span>
    </div>
    <div className="font-display font-bold text-2xl tracking-tighter text-white">{value}</div>
  </div>
);
